export const CONNECTOR_LABEL_FONT_SIZE = 12;
export const CONNECTOR_LABEL_FONT_FAMILY = 'system-ui, -apple-system, sans-serif';
export const CONNECTOR_LABEL_LINE_HEIGHT = 1.2;
export const CONNECTOR_LABEL_CHIP_PAD_X = 4;
export const CONNECTOR_LABEL_CHIP_PAD_Y = 0;
export const CONNECTOR_LABEL_FILL = '#374151';
export const CONNECTOR_LABEL_MASK_BLEED = 1;
export const CONNECTOR_LABEL_BOUNDS_NUDGE_Y = 1;

export interface ConnectorLabelChipOptions {
  fontSize?: number;
  strokeWidth?: number;
  isBezier?: boolean;
  props?: Record<string, unknown>;
}

function formatExportNumber(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function estimateLabelTextWidth(text: string, fontSize: number): number {
  const perChar = fontSize * 0.56;
  return Math.max(perChar, text.length * perChar);
}

function resolveConnectorLabelDecorationPadY(props?: Record<string, unknown>): number {
  if (!props) return 0;
  const heavyDecorationFlags = [
    'sexualAbuse',
    'physicalAbuse',
    'emotionalAbuse',
    'abusePattern',
    'closeHostile',
    'distantViolence',
    'closeViolence',
    'fusedHostile',
    'distanceHostile',
    'focusedNegatively',
    'wave',
  ];
  if (heavyDecorationFlags.some((flag) => props[flag] === true)) return 4;
  if (props.doubleLine === true || props.tripleLine === true) return 2;
  return 0;
}

function resolveConnectorLabelChipPadY(props?: Record<string, unknown>): number {
  return CONNECTOR_LABEL_CHIP_PAD_Y + resolveConnectorLabelDecorationPadY(props);
}

function snapOutwardRect(
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number } {
  const left = Math.floor(x);
  const top = Math.floor(y);
  const right = Math.ceil(x + width);
  const bottom = Math.ceil(y + height);
  return {
    x: left,
    y: top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  };
}

export function computeConnectorLabelChipBounds(
  x: number,
  y: number,
  text: string,
  options: ConnectorLabelChipOptions = {},
): { x: number; y: number; width: number; height: number } {
  const fontSize = options.fontSize ?? CONNECTOR_LABEL_FONT_SIZE;
  const lines = text.split('\n');
  const textWidth = lines.reduce(
    (maxWidth, line) => Math.max(maxWidth, estimateLabelTextWidth(line, fontSize)),
    0,
  );
  const lineHeightPx = Math.ceil(fontSize * CONNECTOR_LABEL_LINE_HEIGHT);
  const textHeight =
    lines.length <= 1
      ? fontSize
      : fontSize + (lines.length - 1) * lineHeightPx;
  const padY = resolveConnectorLabelChipPadY(options.props);
  const totalWidth = Math.ceil(textWidth) + CONNECTOR_LABEL_CHIP_PAD_X;
  const totalHeight = textHeight + padY;
  const rectX = x - totalWidth / 2;
  const rectY = y - totalHeight / 2 - CONNECTOR_LABEL_BOUNDS_NUDGE_Y;
  const bleed = CONNECTOR_LABEL_MASK_BLEED;
  return snapOutwardRect(rectX - bleed, rectY - bleed, totalWidth + bleed * 2, totalHeight + bleed * 2);
}

export const EXPORT_CONNECTOR_LABEL_MASK_ID = 'export-connector-label-mask';

export function renderConnectorLabelMaskDef(
  maskId: string,
  bounds: { x: number; y: number; width: number; height: number },
): string {
  return renderCombinedConnectorLabelMaskDef(maskId, [bounds]);
}

export function renderCombinedConnectorLabelMaskDef(
  maskId: string,
  boundsList: Array<{ x: number; y: number; width: number; height: number }>,
): string {
  if (boundsList.length === 0) return '';
  const holes = boundsList
    .map((bounds) => (
      `<rect x="${formatExportNumber(bounds.x)}" y="${formatExportNumber(bounds.y)}" width="${formatExportNumber(bounds.width)}" height="${formatExportNumber(bounds.height)}" fill="black" shape-rendering="crispEdges" />`
    ))
    .join('');
  return (
    `<defs><mask id="${escapeAttr(maskId)}" maskUnits="userSpaceOnUse">` +
    `<rect x="-100000" y="-100000" width="200000" height="200000" fill="white" />` +
    holes +
    `</mask></defs>`
  );
}

export function renderConnectorLabelHoleFill(
  bounds: { x: number; y: number; width: number; height: number },
  background: string,
): string {
  return (
    `<rect x="${formatExportNumber(bounds.x)}" y="${formatExportNumber(bounds.y)}" ` +
    `width="${formatExportNumber(bounds.width)}" height="${formatExportNumber(bounds.height)}" ` +
    `fill="${escapeAttr(background)}" stroke="none" shape-rendering="crispEdges" />`
  );
}

export function renderCombinedConnectorLabelHoleFills(
  boundsList: Array<{ x: number; y: number; width: number; height: number }>,
  background: string,
): string {
  if (boundsList.length === 0) return '';
  return boundsList.map((bounds) => renderConnectorLabelHoleFill(bounds, background)).join('');
}

export function renderConnectorLabelText(
  x: number,
  y: number,
  text: string,
  options: { fontSize?: number; fontFamily?: string; fill?: string } = {},
): string {
  const fontSize = options.fontSize ?? CONNECTOR_LABEL_FONT_SIZE;
  const fontFamily = options.fontFamily ?? CONNECTOR_LABEL_FONT_FAMILY;
  const fill = options.fill ?? CONNECTOR_LABEL_FILL;
  const lines = text.split('\n');
  const lineHeightPx = Math.ceil(fontSize * CONNECTOR_LABEL_LINE_HEIGHT);
  const firstLineY = y - ((lines.length - 1) * lineHeightPx) / 2;
  const textMarkup = lines
    .map((line, index) => (
      `<tspan x="${formatExportNumber(x)}" y="${formatExportNumber(firstLineY + index * lineHeightPx)}">${escapeText(line)}</tspan>`
    ))
    .join('');
  return (
    `<text text-anchor="middle" dominant-baseline="middle" font-family="${escapeAttr(fontFamily)}" font-size="${fontSize}" fill="${escapeAttr(fill)}">${textMarkup}</text>`
  );
}

export function finalizeConnectorExportWithLabel(
  defs: string,
  connectorBody: string,
  label: {
    x: number;
    y: number;
    text: string;
    strokeWidth?: number;
    isBezier?: boolean;
    props?: Record<string, unknown>;
    connectorId?: string;
  },
): { geometrySvg: string; labelSvg: string; bounds: { x: number; y: number; width: number; height: number } } {
  const bounds = computeConnectorLabelChipBounds(label.x, label.y, label.text, {
    strokeWidth: label.strokeWidth,
    isBezier: label.isBezier,
    props: label.props,
  });
  const labelSvg = renderConnectorLabelText(label.x, label.y, label.text);
  return {
    geometrySvg: `${defs}${connectorBody}`,
    labelSvg,
    bounds,
  };
}
